'use client';

import { FC, useMemo } from 'react';

import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { useLocale } from '@ravago/shared/translations/client';

interface Props {
  id: string;
  label: string;
  countries: Array<string>;
  selectedCountryCode?: string;
  onChange: (countryCode: string) => void;
  disabled?: boolean;
  attributes?: { [key: string]: string };
}

const getLocalizedCountryName = (locale: string, countryCode: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode.toUpperCase()) ?? countryCode.toUpperCase();
  } catch {
    return countryCode.toUpperCase();
  }
};

export const CountryDropdown: FC<Props> = ({ id, label, countries, selectedCountryCode, onChange, disabled, attributes }) => {
  const locale = useLocale();

  const countryOptions = useMemo<DropdownOption[]>(
    () =>
      countries
        .map((countryCode) => ({
          id: countryCode,
          value: getLocalizedCountryName(locale, countryCode),
          icon: countryCode
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [countries, locale]
  );

  const selectedCountry = useMemo(
    () => (selectedCountryCode ? countryOptions.find((option) => option.id === selectedCountryCode.toUpperCase()) : undefined),
    [countryOptions, selectedCountryCode]
  );

  return (
    <Dropdown
      id={id}
      attributes={attributes}
      emptyOption={false}
      label={label}
      value={selectedCountry}
      iconType="flag"
      leadingIcon={selectedCountry?.id}
      options={countryOptions}
      onChange={(option) => option && onChange(option.id)}
      disabled={disabled}
    />
  );
};

export default CountryDropdown;
